// Draws a player's build on a <canvas>: the theme base with the layers of
// every won item. Mirrors BuildImage in the app's twenty_challenge_visuals.dart
// (planned stickers with a white outline and a soft shadow, full-frame
// scenes, and stacks that grow upward). Everything here is synchronous: the
// caller loads the images first (see neededImages).

import { Offset, Rect } from './geom';
import {
  basePath,
  hasPlannedLayout,
  layerPath,
  planScene,
  stackFrame,
  type LayerSpec,
  type ThemeVisual,
} from './visual';

export type Images = Map<string, HTMLImageElement>;

export interface BuildDraw {
  visual: ThemeVisual;
  won: number[]; // theme item indices, in the order they were won
  moved: Map<number, Offset>; // dragged item → its centre, canvas px
  popIn?: { itemIndex: number; t: number } | null; // t: 0..1
  sticker?: boolean;
}

/// A drawn item, for drag hit tests. All in canvas px (the picture's own
/// coordinate system: the base for scenes, the stack frame offset by
/// [origin] for stacks).
export interface DrawnItem {
  itemIndex: number;
  planned: Rect; // where the planner put it (frame coords)
  at: Rect; // where it is drawn (frame coords)
  bounds: Rect; // it must stay inside (frame coords)
  origin: Offset; // frame → canvas
}

export interface DrawResult {
  items: DrawnItem[];
  frameSide: number; // canvas px the square output spans
}

/// URLs [draw] needs.
export function neededImages(d: BuildDraw): string[] {
  const v = d.visual;
  const urls = [basePath(v)];
  for (const i of d.won) if (v.layers.has(i)) urls.push(layerPath(v, i));
  if (v.model === 'stack' && v.cap) urls.push(`${v.dir}/${v.cap.file}`);
  return urls;
}

export function aspectOf(v: ThemeVisual): number {
  return v.model === 'stack' ? 1 : v.width / v.height;
}

const easeOutBack = (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

function popScale(d: BuildDraw, itemIndex: number): number {
  if (!d.popIn || d.popIn.itemIndex !== itemIndex) return 1;
  return 0.6 + 0.4 * easeOutBack(Math.min(1, Math.max(0, d.popIn.t)));
}

/// [items] in paint order with the moved ones last, in [moved] order.
function movedLast<T>(items: T[], id: (t: T) => number, moved: Map<number, Offset>): T[] {
  return [
    ...items.filter((it) => !moved.has(id(it))),
    ...[...moved.keys()].flatMap((i) => items.filter((it) => id(it) === i)),
  ];
}

/// [shift] cut so [rect] moved by it stays inside [bounds].
export function keep(rect: Rect, shift: Offset, bounds: Rect): Offset {
  const cl = (v: number, a: number, b: number) => Math.min(Math.max(v, a), b);
  return new Offset(
    cl(shift.dx, Math.min(0, bounds.left - rect.left), Math.max(0, bounds.right - rect.right)),
    cl(shift.dy, Math.min(0, bounds.top - rect.top), Math.max(0, bounds.bottom - rect.bottom)),
  );
}

/// Where an item planned at [rect] is drawn when dragged to [centre].
export function placedAt(rect: Rect, centre: Offset | undefined, origin: Offset, bounds: Rect): Rect {
  if (!centre) return rect;
  return rect.shift(keep(rect, centre.minus(origin).minus(rect.center), bounds));
}

/// Draws the build into [ctx], filling a [px] x [px] square (planned scenes
/// and stacks; full-frame scenes keep their aspect, which is square for
/// every current theme).
export function drawBuild(ctx: CanvasRenderingContext2D, px: number, d: BuildDraw, imgs: Images): DrawResult {
  const v = d.visual;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.fillStyle = v.backdrop;
  ctx.fillRect(0, 0, px, px);
  let res: DrawResult;
  if (v.model === 'stack' && v.stack) res = drawStack(ctx, px, d, imgs);
  else if (hasPlannedLayout(v)) res = drawPlanned(ctx, px, d, imgs);
  else res = drawScene(ctx, px, d, imgs);
  ctx.restore();
  return res;
}

function img(imgs: Images, src: string): HTMLImageElement | null {
  const i = imgs.get(src);
  return i && i.complete && i.naturalWidth > 0 ? i : null;
}

function drawPlanned(ctx: CanvasRenderingContext2D, px: number, d: BuildDraw, imgs: Images): DrawResult {
  const v = d.visual;
  const s = px / v.width;
  const base = img(imgs, basePath(v));
  if (base) ctx.drawImage(base, 0, 0, px, (v.height * px) / v.width);
  const bounds = Rect.fromLTWH(0, 0, v.width, v.height);
  const placed = planScene(v, d.won);
  const out: DrawnItem[] = [];
  for (const p of movedLast(placed, (p) => p.itemIndex, d.moved)) {
    const at = placedAt(p.rect, d.moved.get(p.itemIndex), Offset.zero, bounds);
    out.push({ itemIndex: p.itemIndex, planned: p.rect, at, bounds, origin: Offset.zero });
    const layer = img(imgs, layerPath(v, p.itemIndex));
    const spec = v.layers.get(p.itemIndex)!;
    if (!layer || !spec.box) continue;
    const k = popScale(d, p.itemIndex);
    const r = k === 1 ? at : Rect.fromCenter(at.center, at.width * k, at.height * k);
    if (d.sticker === false) {
      ctx.drawImage(alphaCut(layer, spec.box), r.left * s, r.top * s, r.width * s, r.height * s);
    } else {
      drawSticker(ctx, alphaCut(layer, spec.box), r, s, v.width);
    }
  }
  return { items: out, frameSide: v.width };
}

function drawScene(ctx: CanvasRenderingContext2D, px: number, d: BuildDraw, imgs: Images): DrawResult {
  const v = d.visual;
  const h = (v.height * px) / v.width;
  const base = img(imgs, basePath(v));
  if (base) ctx.drawImage(base, 0, 0, px, h);
  const order = d.won.filter((i) => v.layers.has(i)).sort((a, b) => v.layers.get(a)!.z - v.layers.get(b)!.z);
  for (const i of order) {
    const l = img(imgs, layerPath(v, i));
    if (l) ctx.drawImage(l, 0, 0, px, h);
  }
  return { items: [], frameSide: v.width };
}

function drawStack(ctx: CanvasRenderingContext2D, px: number, d: BuildDraw, imgs: Images): DrawResult {
  const v = d.visual;
  const g = v.stack!;
  const f = stackFrame(v, d.won);
  const s = px / f.side;
  const w = v.width;
  const h = v.height;
  const baseR = Rect.fromLTWH(-f.left, -f.top, w, h);
  const padL = baseR.left > 0;
  const padR = baseR.right < f.side;
  const padT = baseR.top > 0;
  const base = img(imgs, basePath(v));
  const sc = (r: Rect): [number, number, number, number] => [r.left * s, r.top * s, r.width * s, r.height * s];
  if (base) {
    if (padL || padR || padT) {
      // Where the frame reaches past the base, its edge strips are
      // stretched out and blurred so the photo continues.
      const e = 8;
      ctx.save();
      if ('filter' in ctx) ctx.filter = `blur(${w * 0.02 * s}px)`;
      ctx.drawImage(base, ...sc(baseR));
      const strip = (dst: Rect, src: Rect) =>
        ctx.drawImage(base, src.left, src.top, src.width, src.height, ...sc(dst));
      if (padL) strip(Rect.fromLTRB(0, baseR.top, baseR.left, baseR.bottom), Rect.fromLTWH(0, 0, e, h));
      if (padR) strip(Rect.fromLTRB(baseR.right, baseR.top, f.side, baseR.bottom), Rect.fromLTWH(w - e, 0, e, h));
      if (padT) strip(Rect.fromLTRB(baseR.left, 0, baseR.right, baseR.top), Rect.fromLTWH(0, 0, w, e));
      if (padT && padL) strip(Rect.fromLTRB(0, 0, baseR.left, baseR.top), Rect.fromLTWH(0, 0, e, e));
      if (padT && padR) strip(Rect.fromLTRB(baseR.right, 0, f.side, baseR.top), Rect.fromLTWH(w - e, 0, e, e));
      ctx.restore();
      ctx.drawImage(fadedBase(base, padL, padR, padT), ...sc(baseR));
    } else {
      ctx.drawImage(base, ...sc(baseR));
    }
  }
  const origin = new Offset(f.left, f.top);
  const bounds = Rect.fromLTWH(0, 0, f.side, f.side);
  const out: DrawnItem[] = [];
  for (const p of movedLast(f.placed, (p) => p.itemIndex, d.moved)) {
    const planned = Rect.fromLTWH(g.cx - p.spec.w / 2 - f.left, p.y - f.top, p.spec.w, p.spec.h);
    const at = placedAt(planned, d.moved.get(p.itemIndex), origin, bounds);
    out.push({ itemIndex: p.itemIndex, planned, at, bounds, origin });
    const sprite = img(imgs, `${v.dir}/${p.spec.file}`);
    if (!sprite) continue;
    const k = popScale(d, p.itemIndex);
    const r = k === 1 ? at : Rect.fromCenter(at.center, at.width * k, at.height * k);
    ctx.drawImage(sprite, ...sc(r));
  }
  return { items: out, frameSide: f.side };
}

// ─── Sticker look ──────────────────────────────────────────────────────────

const cutCache = new WeakMap<HTMLImageElement, Map<string, HTMLCanvasElement>>();

/// The [box] region of a full-frame layer with the alpha cut of the app:
/// alpha' = (alpha - 40) * 255 / 215, colour untouched. Alpha below 40 is
/// matte residue that would otherwise dilate into the outline.
function alphaCut(layer: HTMLImageElement, box: Rect): HTMLCanvasElement {
  let byBox = cutCache.get(layer);
  if (!byBox) cutCache.set(layer, (byBox = new Map()));
  const key = `${box.left},${box.top},${box.width},${box.height}`;
  const hit = byBox.get(key);
  if (hit) return hit;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(box.width));
  c.height = Math.max(1, Math.round(box.height));
  const x = c.getContext('2d', { willReadFrequently: true })!;
  x.drawImage(layer, box.left, box.top, box.width, box.height, 0, 0, c.width, c.height);
  try {
    const data = x.getImageData(0, 0, c.width, c.height);
    const a = data.data;
    for (let i = 3; i < a.length; i += 4) a[i] = Math.max(0, Math.min(255, ((a[i] - 40) * 255) / 215));
    x.putImageData(data, 0, 0);
  } catch {
    // Tainted canvas (should not happen, the content is same-origin).
  }
  byBox.set(key, c);
  return c;
}

const stickerCache = new Map<string, HTMLCanvasElement>();
let stickerIds = new WeakMap<HTMLCanvasElement, number>();
let nextStickerId = 1;

/// Draws [crop] into [r] (canvas px, scale [s]) as a sticker: a white
/// outline of 0.004 W following the alpha shape over a soft drop shadow.
function drawSticker(ctx: CanvasRenderingContext2D, crop: HTMLCanvasElement, r: Rect, s: number, W: number) {
  const outline = W * 0.004 * s;
  const sigma = W * 0.004 * s;
  const dw = Math.max(1, Math.round(r.width * s));
  const dh = Math.max(1, Math.round(r.height * s));
  const pad = Math.ceil(outline + 2);
  let id = stickerIds.get(crop);
  if (!id) stickerIds.set(crop, (id = nextStickerId++));
  const key = `${id}|${dw}x${dh}|${outline.toFixed(2)}`;
  let st = stickerCache.get(key);
  if (!st) {
    st = document.createElement('canvas');
    st.width = dw + 2 * pad;
    st.height = dh + 2 * pad;
    const x = st.getContext('2d')!;
    // White silhouette of the item at its drawn size.
    const sil = document.createElement('canvas');
    sil.width = dw;
    sil.height = dh;
    const sx = sil.getContext('2d')!;
    sx.drawImage(crop, 0, 0, dw, dh);
    sx.globalCompositeOperation = 'source-in';
    sx.fillStyle = '#fff';
    sx.fillRect(0, 0, dw, dh);
    // Dilate by stamping the silhouette around two rings.
    const steps = 24;
    for (const rr of [outline, outline * 0.5]) {
      for (let k = 0; k < steps; k++) {
        const a = (k / steps) * Math.PI * 2;
        x.drawImage(sil, pad + Math.cos(a) * rr, pad + Math.sin(a) * rr);
      }
    }
    x.drawImage(crop, pad, pad, dw, dh);
    if (stickerCache.size > 160) stickerCache.delete(stickerCache.keys().next().value!);
    stickerCache.set(key, st);
  }
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = sigma * 2;
  ctx.shadowOffsetY = W * 0.0025 * s;
  ctx.drawImage(st, r.left * s - pad, r.top * s - pad, dw + 2 * pad, dh + 2 * pad);
  ctx.restore();
}

/// Clears the per-size sticker cache (on theme change).
export function clearStickerCache(): void {
  stickerCache.clear();
  stickerIds = new WeakMap();
}

const fadeCache = new WeakMap<HTMLImageElement, Map<string, HTMLCanvasElement>>();

/// [base] fading out over its outer 6 % on the padded sides.
function fadedBase(base: HTMLImageElement, l: boolean, r: boolean, t: boolean): HTMLCanvasElement {
  let m = fadeCache.get(base);
  if (!m) fadeCache.set(base, (m = new Map()));
  const key = `${l}${r}${t}`;
  const hit = m.get(key);
  if (hit) return hit;
  const c = document.createElement('canvas');
  c.width = base.naturalWidth;
  c.height = base.naturalHeight;
  const x = c.getContext('2d')!;
  x.drawImage(base, 0, 0);
  x.globalCompositeOperation = 'destination-in';
  if (l || r) {
    const gr = x.createLinearGradient(0, 0, c.width, 0);
    gr.addColorStop(0, l ? 'rgba(0,0,0,0)' : '#000');
    gr.addColorStop(0.06, '#000');
    gr.addColorStop(0.94, '#000');
    gr.addColorStop(1, r ? 'rgba(0,0,0,0)' : '#000');
    x.fillStyle = gr;
    x.fillRect(0, 0, c.width, c.height);
  }
  if (t) {
    const gr = x.createLinearGradient(0, 0, 0, c.height);
    gr.addColorStop(0, 'rgba(0,0,0,0)');
    gr.addColorStop(0.06, '#000');
    gr.addColorStop(1, '#000');
    x.fillStyle = gr;
    x.fillRect(0, 0, c.width, c.height);
  }
  m.set(key, c);
  return c;
}

export type { LayerSpec };
